import { registerPlugin } from '@capacitor/core'

import { paymentsApi } from '@/api/client'
//import StorageFactory from '@/drivers/storage'
import { logger } from '@/monitoring'

import type { AdyenChargeResponse, AdyenCredentials } from './types'

type PaymentResponse = {
  tenderReference: string
  pspReference: string
  amount: number
  tip: number
}

type PaymentParams = {
  paymentId: string
  amount: number
  tabId: string
  poiid: string
}

interface AdyenTerminalApiPlugin {
  charge(params: {
    nexoRequest: string
    identifier: string
    passphrase: string
    poiid: string
    version: number
  }): Promise<AdyenChargeResponse>
}

const plugin = registerPlugin<AdyenTerminalApiPlugin>('AdyenTerminalPlugin')

//const STORAGE_KEY = 'adyenCredentials'

class AdyenTerminalApi {
  private available = false

  async charge({ paymentId, amount, tabId, poiid }: PaymentParams): Promise<PaymentResponse> {
    if (!poiid) {
      throw new Error('POIID is not set')
    }

    const credentials = await this.getAuthCredentials(poiid)
    if (!credentials) {
      throw new Error('No Adyen credentials found')
    }

    try {
      const request = await paymentsApi.getNexoRequest({
        getNexoRequestRequest: {
          paymentId,
          amount,
          tabId,
          poiid,
          source: 'dataphone'
        }
      })
      if (!request) {
        throw new Error('Failed to get request')
      }

      const response = await plugin.charge({
        nexoRequest: request,
        identifier: credentials.identifier,
        passphrase: credentials.passphrase,
        poiid,
        version: credentials.version
      })
      const paymentResponse = response.SaleToPOIResponse.PaymentResponse
      if (paymentResponse.Response.Result !== 'Success') {
        throw new Error(paymentResponse.Response.ErrorCondition || 'Payment failed')
      }

      const tip = (paymentResponse.PaymentResult.AmountsResp.TipAmount || 0) * 100
      if (tip) {
        await this.handleTip(paymentResponse.SaleData.SaleTransactionID.TransactionID, tip)
      }
      const [tenderReference, pspReference] =
        paymentResponse.POIData.POITransactionID.TransactionID.split('.')
      return {
        tenderReference,
        pspReference,
        amount: paymentResponse.PaymentResult.AmountsResp.AuthorizedAmount * 100,
        tip
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Unknown error during payment processing')
    }
  }

  async init(): Promise<void> {
    // Initialization logic can be added here if needed in the future
    this.available = true
  }

  isAvailable(): boolean {
    return this.available
  }

  private async getAuthCredentials(poiid: string): Promise<AdyenCredentials | undefined> {
    /* const storage = StorageFactory.getStorage()

    const storedCredentials =
      await storage.getItem<AdyenCredentials>(STORAGE_KEY)
    if (storedCredentials) {
      return storedCredentials
    } */

    try {
      const credentials = await this.getCredentials(poiid)
      /*  if (credentials) {
        await storage.setItem(STORAGE_KEY, credentials)
      } */
      return credentials
    } catch (_) {
      logger.error('Failed to get authentication credentials')
      return undefined
    }
  }

  private async getCredentials(poiid: string): Promise<AdyenCredentials> {
    try {
      return await paymentsApi.getAdyenCredentials({ poiid })
    } catch (_) {
      throw new Error('Unable to get Terminal credentials')
    }
  }

  private async handleTip(paymentId: string, tip: number): Promise<void> {
    await paymentsApi.updatePaymentTip({
      updatePaymentTipRequest: { paymentId, tipAmount: tip }
    })
  }
}

export const AdyenTerminalApiPlugin = new AdyenTerminalApi()
